import {ChangeDetectionStrategy, Component, DestroyRef, inject, input, linkedSignal, output} from '@angular/core';
import {debounceTime, Subject} from 'rxjs';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {IconComponent} from '@shared/icon';

@Component({
	selector: 'dc-search-input',
	imports: [IconComponent],
	templateUrl: './search-input.component.html',
	styleUrl: './search-input.component.scss',
	changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SearchInputComponent {
	// Using the new input() API
	readonly initialTerm = input<string | null>(null); // Optional input

	// --- Inputs ---
	readonly placeholder = input<string>(''); // Optional input with default
	readonly debounceMs = input<number>(300); // Configurable debounce time
	// Using the new output() API
	readonly termChanged = output<string>(); // Emits debounced search term

	// --- Outputs ---
	// --- Component State Signals ---
	protected currentTerm = linkedSignal(() => this.initialTerm() ?? '');
	// --- Injected Services ---
	private readonly destroyRef = inject(DestroyRef);
	// --- Private Properties ---
	private readonly searchSubject = new Subject<string>(); // Subject for debounce logic

	// --- Constructor ---
	// Constructor usually empty when using inject() extensively
	constructor() {
		this.setupDebounce();
	}

	// --- Methods ---

	/** Handles the input event from the text field */
	protected onInputChange(event: Event): void {
		const target = event.target as HTMLInputElement;
		const newValue = target.value;
		this.currentTerm.set(newValue); // Update internal signal immediately
		this.searchSubject.next(newValue); // Push the new value to the debounce subject
	}

	/** Sets up the debounce logic for search term emission */
	private setupDebounce(): void {
		this.searchSubject
			.pipe(
				debounceTime(this.debounceMs()), // Use the input for debounce time
				takeUntilDestroyed(this.destroyRef), // Auto-unsubscribe on component destruction
			)
			.subscribe(term => {
				this.termChanged.emit(term); // Emit the debounced term via the output
			});
	}
}
